import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * Текстовое поле, автоматически подстраивающее высоту под количество текста,
 * но не превышающее разумный предел (maxHeight), с возможностью вертикальной
 * прокрутки при превышении предела и без ручного растягивания (resize-none).
 */
const AutoResizeTextarea = React.forwardRef(({
  value,
  minHeight = 44,
  maxHeight = 180,
  className = '',
  onChange,
  rows = 2,
  style = {},
  ...props
}, ref) => {
  const innerRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const resize = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const scrollH = el.scrollHeight;
    const targetH = Math.min(Math.max(scrollH, minHeight), maxHeight);
    el.style.height = `${targetH}px`;
    el.style.overflowY = scrollH > maxHeight ? 'auto' : 'hidden';
  }, [minHeight, maxHeight]);

  useEffect(() => {
    resize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', resize);
      return () => window.removeEventListener('resize', resize);
    }
  }, [value, resize]);

  return (
    <textarea
      ref={innerRef}
      value={value}
      rows={rows}
      onChange={(event) => {
        onChange?.(event);
        resize();
      }}
      className={`resize-none transition-[height] duration-75 ${className}`.trim()}
      style={{
        ...style,
        minHeight: `${minHeight}px`,
        maxHeight: `${maxHeight}px`,
      }}
      {...props}
    />
  );
});

AutoResizeTextarea.displayName = 'AutoResizeTextarea';

export default AutoResizeTextarea;
